import React from 'react';
import { cn } from '../../lib/utils';
import { Circle } from 'lucide-react';

interface RadioGroupContextType {
    value: string;
    onValueChange: (value: string) => void;
}

const RadioGroupContext = React.createContext<RadioGroupContextType | null>(null);

interface RadioGroupProps {
    value: string;
    onValueChange: (value: string) => void;
    children: React.ReactNode;
    className?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
    value,
    onValueChange,
    children,
    className
}) => {
    return (
        <RadioGroupContext.Provider value={{ value, onValueChange }}>
            <div className={cn("grid gap-2", className)} role="radiogroup">
                {children}
            </div>
        </RadioGroupContext.Provider>
    );
};

interface RadioGroupItemProps {
    value: string;
    id?: string;
    className?: string;
}

export const RadioGroupItem: React.FC<RadioGroupItemProps> = ({
    value,
    id,
    className
}) => {
    const context = React.useContext(RadioGroupContext);
    if (!context) throw new Error('RadioGroupItem must be used within RadioGroup');

    const isSelected = context.value === value;

    return (
        <button
            type="button"
            role="radio"
            aria-checked={isSelected}
            id={id}
            onClick={() => context.onValueChange(value)}
            className={cn(
                "aspect-square h-4 w-4 rounded-full border border-slate-900 dark:border-slate-300 text-slate-900 dark:text-slate-100 ring-offset-white dark:ring-offset-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 dark:focus-visible:ring-slate-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center",
                className
            )}
        >
            {isSelected && <Circle className="h-2.5 w-2.5 fill-current text-current" />}
        </button>
    );
};
